import { Link, useParams, Navigate } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteConfig, categories } from '@/mocks/siteData';
import { getComicBySlug, getComicsByCategory, ComicArticle } from '@/mocks/comics';

function ComicCard({ comic }: { comic: ComicArticle }) {
  return (
    <Link
      to={`/article/${comic.slug}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩国漫画`}
          title={`${comic.title} - 好看的韩国漫画免费在线观看`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            comic.status === '连载中'
              ? 'bg-accent-500 text-background-50'
              : 'bg-secondary-500 text-background-50'
          }`}>
            {comic.status}
          </span>
        </div>
      </div>
      <div className="p-3">
        <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1 font-heading">
          {comic.title}
        </h4>
        <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{comic.author}</p>
      </div>
    </Link>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = getComicBySlug(slug || '');

  if (!comic) {
    return <Navigate to="/" replace />;
  }

  const relatedComics = getComicsByCategory(comic.categorySlug).filter(c => c.id !== comic.id).slice(0, 4);
  const categoryName = categories.find(c => c.slug === comic.categorySlug)?.name || comic.category;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - ${siteConfig.name}`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: comic.publishDate,
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${comic.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      { '@type': 'ListItem', position: 2, name: `${categoryName}漫画`, item: `${siteConfig.url}/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

      <article className="max-w-4xl mx-auto px-4 md:px-6 pt-4 pb-12">
        <Breadcrumb
          items={[
            { label: `${categoryName}漫画`, href: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]}
        />

        <div className="flex flex-col md:flex-row gap-6 mt-4">
          <div className="flex-shrink-0 w-full md:w-[280px]">
            <div className="relative aspect-[3/4] rounded-xl overflow-hidden">
              <img
                src={comic.coverImage}
                alt={`${comic.title} - ${categoryName}漫画`}
                title={`${comic.title} - 好看的韩国漫画在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <header>
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                >
                  {categoryName}
                </Link>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  comic.status === '连载中'
                    ? 'bg-accent-100 text-accent-700'
                    : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mt-3 font-heading">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">作者：{comic.author}</p>
            </header>

            <div className="flex items-center gap-4 mt-4 py-3 border-y border-background-200/70 flex-wrap">
              <div className="flex items-center gap-1">
                <i className="ri-star-fill text-secondary-500"></i>
                <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                <span className="text-xs text-foreground-400">评分</span>
              </div>
              <div className="w-px h-4 bg-background-300"></div>
              <div className="flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-eye-line text-foreground-400"></i>
                <span>{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读</span>
              </div>
              <div className="w-px h-4 bg-background-300"></div>
              <div className="flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-file-copy-line text-foreground-400"></i>
                <span>共 {comic.chapters} 话</span>
              </div>
              <div className="w-px h-4 bg-background-300"></div>
              <div className="flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-calendar-line text-foreground-400"></i>
                <span>更新于 {comic.updateDate}</span>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {comic.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/50"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Link
                to={`/category/${comic.categorySlug}`}
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-line mr-1.5"></i>开始阅读
              </Link>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 hover:bg-background-100 transition-colors cursor-pointer" aria-label="收藏">
                <i className="ri-bookmark-line text-foreground-600"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-300 hover:bg-background-100 transition-colors cursor-pointer" aria-label="分享">
                <i className="ri-share-line text-foreground-600"></i>
              </button>
            </div>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">剧情简介</h2>
          <div
            className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: comic.content }}
          />
        </section>

        {relatedComics.length > 0 && (
          <section className="mt-12 pt-8 border-t border-background-200/70">
            <h2 className="text-lg font-bold text-foreground-900 font-heading mb-4">
              更多{categoryName}漫画推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {relatedComics.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </section>
        )}
      </article>
    </Layout>
  );
}